import React from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  HubShell,
  StepTabs,
  ConfirmationToggle,
  StepIntroNote,
  PrerequisiteBanner,
  PrerequisiteDialog,
  RequestEditButton,
} from './HubShell'
import type { HubColor, HubStep } from './HubShell'

export interface HubTemplateStep {
  id: string
  label: string
  icon: LucideIcon
  fieldsComplete: boolean
  confirmation: {
    label: string
    checked: boolean
    onChange: (checked: boolean) => void
  }
  intro?: {
    title?: string
    body: React.ReactNode
  }
  content: React.ReactNode
}

export interface HubTemplateProps {
  icon: LucideIcon
  color: HubColor
  title: string
  subtitle: string
  onClose: () => void
  submitted: boolean
  completionPercent: number
  prerequisitesMet: boolean
  prerequisiteMessage?: string
  prerequisiteHubName?: string
  steps: HubTemplateStep[]
  activeStepId: string
  onStepChange: (id: string) => void
  sectionLabel?: string
  canSubmit?: boolean
  onSubmit: () => void
  submitLabel?: string
  submitIcon?: LucideIcon
  completedMessage?: string
  nextHubMessage?: string
  lastSavedAt?: string | null
  showSaveIndicator?: boolean
  onRequestEdit?: (reason: string) => void
}

export const HubTemplate: React.FC<HubTemplateProps> = ({
  icon,
  color,
  title,
  subtitle,
  onClose,
  submitted,
  completionPercent,
  prerequisitesMet,
  prerequisiteMessage,
  prerequisiteHubName,
  steps,
  activeStepId,
  onStepChange,
  sectionLabel = 'Quick Steps',
  canSubmit: canSubmitOverride,
  onSubmit,
  submitLabel = 'Submit Hub',
  submitIcon,
  completedMessage = 'Hub Completed',
  nextHubMessage,
  lastSavedAt,
  showSaveIndicator = true,
  onRequestEdit,
}) => {
  const activeStep = steps.find((step) => step.id === activeStepId) ?? steps[0]

  const derivedCanSubmit = steps.every(
    (step) => step.fieldsComplete && step.confirmation.checked
  )
  const canSubmit = canSubmitOverride !== undefined ? canSubmitOverride : derivedCanSubmit

  const hubSteps: HubStep[] = steps.map((step) => ({
    key: step.id,
    label: step.label,
    icon: step.icon,
    completed: step.fieldsComplete && step.confirmation.checked,
  }))

  const footerExtra = submitted && onRequestEdit ? (
    <RequestEditButton
      onRequest={onRequestEdit}
      color={color}
    />
  ) : undefined

  if (!prerequisitesMet) {
    return (
      <PrerequisiteDialog
        open
        hubTitle={title}
        message={
          prerequisiteMessage ??
          `Complete the ${prerequisiteHubName ?? 'previous hub'} first to unlock this section.`
        }
        color={color}
        onClose={onClose}
      />
    )
  }

  return (
    <HubShell
      icon={icon}
      title={title}
      subtitle={subtitle}
      completionPercent={completionPercent}
      isSubmitted={submitted}
      color={color}
      onClose={onClose}
      canSubmit={canSubmit}
      completedMessage={completedMessage}
      nextHubMessage={nextHubMessage}
      onSubmit={onSubmit}
      submitLabel={submitLabel}
      submitIcon={submitIcon}
      lastSavedAt={lastSavedAt}
      showSaveIndicator={showSaveIndicator}
      footerExtra={footerExtra}
    >
      <StepTabs
        steps={hubSteps}
        activeStep={activeStepId}
        onStepChange={onStepChange}
        sectionTitle={sectionLabel}
        color={color}
        isSubmitted={submitted}
      />
      {activeStep.intro && (
        <StepIntroNote
          icon={activeStep.icon}
          title={activeStep.intro.title}
          body={activeStep.intro.body}
          color={color}
        />
      )}
      <div className="flex-1">
        {activeStep.content}
      </div>
      <ConfirmationToggle
        id={`confirm-${activeStep.id}`}
        label={activeStep.confirmation.label}
        checked={submitted || activeStep.confirmation.checked}
        onChange={activeStep.confirmation.onChange}
        visible
        disabled={submitted || !activeStep.fieldsComplete}
      />
    </HubShell>
  )
}

export { PrerequisiteBanner }
